import useTranslation from 'next-translate/useTranslation'
import { useMemo } from 'react'

import { AugmentedSquareFootageInfo } from '../../../augmented-square-footage-info/augmented-square-footage-info'
import {
  ResidentialApartmentPropertyDetails,
  ResidentialPropertyForSaleListing,
} from '@unreserved-frontend-v2/api/generated/graphql/types'
import { DetailsTableRow } from '@unreserved-frontend-v2/ui/details-table/models'
import { faRuler } from '@fortawesome/pro-light-svg-icons'
import { useBedsCell } from './use-beds-cell'
import { useBathsCell } from './use-baths-cell'
import { emptyDefault } from '@unreserved-frontend-v2/utils/empty-default'

export const useSizeRow = (
  listing: ResidentialPropertyForSaleListing,
  isMobile = false
): DetailsTableRow | undefined => {
  const { t } = useTranslation()
  const bedsCell = useBedsCell(listing.propertyDetails)
  const bathsCell = useBathsCell(listing.propertyDetails)

  return useMemo(() => {
    let roomsValue = t('listings:details-pages.total-rooms', { count: listing.rooms?.length })
    const stories = listing.propertyDetails?.stories

    if (stories) {
      roomsValue += ` ${t('listings:details-pages.across-floors', { count: stories })}`
    }

    if (
      !listing.propertyDetails?.squareFootage?.approximateSquareFootage &&
      !listing.rooms?.length &&
      !listing.propertyDetails?.beds &&
      !listing.propertyDetails?.baths &&
      !(listing.propertyDetails as ResidentialApartmentPropertyDetails)?.dens
    ) {
      return
    }

    return {
      icon: faRuler,
      title: t('listings:details-pages.size'),
      cells: [
        {
          id: 'size',
          cellClassName: 'col-span-4 md:col-span-4 md:pb-0',
          data: [
            {
              id: 'data-size',
              label:
                !listing.propertyDetails?.squareFootage?.approximateSquareFootage &&
                listing.propertyDetails?.squareFootage?.derivedSquareFootage ? (
                  <AugmentedSquareFootageInfo
                    squareFootage={listing.propertyDetails?.squareFootage}
                    variant={`${isMobile ? 'mobile' : 'desktop'}-details-table`}
                  />
                ) : (
                  t('listings:details-pages.sq-ft', {
                    count: emptyDefault(listing.propertyDetails?.squareFootage?.approximateSquareFootage, 0),
                  })
                ),
              value: roomsValue,
              type: 'headline',
            },
          ],
        },
        bedsCell,
        bathsCell,
      ],
    }
  }, [t, listing.rooms?.length, listing.propertyDetails, isMobile, bedsCell, bathsCell])
}
